import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL } from '../../../utils';
import { formatDate } from '../../../utils/functions';
import axios from 'axios';
import Add from './add';
import Update from './update';
import Delete from './delete';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const Brands = () => {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', date: '' });
  const [draftFilters, setDraftFilters] = useState({ date: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deletingBrand, setDeletingBrand] = useState(null);

  const fetchBrands = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/marques`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBrands(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des marques :', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredBrands = useMemo(() => {
    return (brands || []).filter((brand) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search) {
        const haystack = `${brand.nom || ''} ${brand.reference || ''}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (appliedFilters.date && brand.date_creation) {
        if (new Date(brand.date_creation).toDateString() !== new Date(appliedFilters.date).toDateString()) {
          return false;
        }
      }
      return true;
    });
  }, [brands, appliedFilters]);

  const sortedBrands = useMemo(() => {
    const sorted = [...filteredBrands];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let aValue = a[key] ?? '';
      let bValue = b[key] ?? '';
      if (key === 'auteur') {
        aValue = a.ajouter_par?.name ?? '';
        bValue = b.ajouter_par?.name ?? '';
      }
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredBrands, sortConfig]);

  const stats = useMemo(() => ({ total: filteredBrands.length }), [filteredBrands]);

  const totalPages = Math.max(1, Math.ceil(sortedBrands.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentBrands = sortedBrands.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({ ...prev, date: draftFilters.date }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ date: '' });
    setAppliedFilters({ search: '', date: '' });
    setCurrentPage(1);
    setSortConfig({ key: 'nom', direction: 'asc' });
  };

  const handleRefresh = async () => {
    await fetchBrands();
    setCurrentPage(1);
  };

  const colSpan = isRole13 ? 4 : 5;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Catalogue</p>
          <h1 className="duo-hero-title">Marques</h1>
          <p className="duo-hero-sub mt-2 mb-0">Référencez les marques de vos produits.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des marques
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher une marque..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    title="Date de création"
                    value={draftFilters.date}
                    onChange={(e) => setDraftFilters({ date: e.target.value })}
                  />
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('nom')}>
                    Marque <SortIcon active={isSortActive('nom')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('auteur')}>
                    Ajouté par <SortIcon active={isSortActive('auteur')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('date_creation')}>
                    Date de création{' '}
                    <SortIcon active={isSortActive('date_creation')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentBrands.length > 0 ? (
                  currentBrands.map((brand) => (
                    <tr key={brand.id}>
                      <td>
                        <span className="fw-bold d-block">{brand.nom}</span>
                        {brand.reference && (
                          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {brand.reference}
                          </span>
                        )}
                      </td>
                      <td>{brand.ajouter_par?.name ?? '—'}</td>
                      <td className="text-nowrap">{formatDate(brand.date_creation)}</td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Modifier"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setEditingBrand(brand)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                            <button
                              type="button"
                              title="Supprimer"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setDeletingBrand(brand)}
                            >
                              <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucune marque correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentBrands.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedBrands.length)} sur {sortedBrands.length}{' '}
                entrée{sortedBrands.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      <Add open={showAddModal} onClose={() => setShowAddModal(false)} refreshData={handleRefresh} />
      <Update
        open={!!editingBrand}
        onClose={() => setEditingBrand(null)}
        brand={editingBrand}
        refreshData={handleRefresh}
      />
      <Delete
        open={!!deletingBrand}
        onClose={() => setDeletingBrand(null)}
        brand={deletingBrand}
        refreshData={handleRefresh}
      />
    </MainLayout>
  );
};

export default Brands;
